import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Wrench, AlertOctagon, Package } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="p-6 hover:bg-white/[0.02] transition-all duration-150">
    <div className="flex items-start justify-between">
      <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">{label}</div>
      <Icon className={`w-4 h-4 ${accent || "text-[#A3A3A3]"}`} strokeWidth={2} />
    </div>
    <div className={`mt-4 font-mono-tab text-4xl md:text-5xl font-semibold tracking-tighter ${accent || "text-white"}`}>
      {value}
    </div>
  </div>
);

const TypeBarColors = ["#007AFF", "#F5A623", "#22C55E", "#FF3B30", "#A855F7", "#06B6D4"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [statsRes, chartsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/charts"),
      ]);
      setStats(statsRes.data);
      setCharts(chartsRes.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/seed");
      if (data.seeded) {
        toast.success("Demo data loaded");
        load();
      } else {
        toast.info("Data already exists");
      }
    } catch {
      toast.error("Seed failed");
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-8 text-[#A3A3A3] font-mono-tab text-xs tracking-[0.2em] uppercase">
        Loading telemetry...
      </div>
    );
  }

  const isEmpty = stats?.locations === 0 && stats?.equipment === 0;

  // Compress dates on chart for x-axis
  const trend = (charts?.pm_trend || []).map((d) => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));
  const faultData = charts?.fault_by_type || [];

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-white/10 flex-wrap gap-4">
        <div>
          <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">
            System Overview / {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter uppercase">
            Control <span className="text-[#007AFF]">Dashboard</span>
          </h1>
        </div>
        {isEmpty && (
          <button
            data-testid="seed-demo-btn"
            onClick={seed}
            className="px-5 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium tracking-tight"
          >
            Load Demo Data
          </button>
        )}
      </div>

      {(stats?.open_faults > 0 || stats?.low_parts > 0) && (
        <div className="mb-8 grid md:grid-cols-2 gap-4">
          {stats?.open_faults > 0 && (
            <div data-testid="alert-faults" className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-5 py-4 flex items-center gap-4">
              <AlertOctagon className="w-5 h-5 text-[#FF3B30] shrink-0" />
              <div>
                <div className="font-display font-bold text-[#FF3B30]">
                  {stats.open_faults} open breakdown{stats.open_faults === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-[#E0E0E0]">Review the Breakdowns tab.</div>
              </div>
            </div>
          )}
          {stats?.low_parts > 0 && (
            <div data-testid="alert-parts" className="border border-[#F5A623]/40 bg-[#F5A623]/10 px-5 py-4 flex items-center gap-4">
              <Package className="w-5 h-5 text-[#F5A623] shrink-0" />
              <div>
                <div className="font-display font-bold text-[#F5A623]">
                  {stats.low_parts} part{stats.low_parts === 1 ? "" : "s"} below minimum stock
                </div>
                <div className="text-xs text-[#E0E0E0]">Reorder in the Parts tab.</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/10 mb-8 bg-[#121212] [&>*]:border-r [&>*]:border-white/10 [&>*:last-child]:border-r-0">
        <Kpi label="Locations" value={stats?.locations ?? 0} icon={MapPin} testid="kpi-locations" />
        <Kpi label="Equipment" value={stats?.equipment ?? 0} icon={Wrench} testid="kpi-equipment" />
        <Kpi label="Open Faults" value={stats?.open_faults ?? 0} icon={AlertOctagon} accent={stats?.open_faults > 0 ? "text-[#FF3B30]" : "text-white"} testid="kpi-faults" />
        <Kpi label="Low Parts" value={stats?.low_parts ?? 0} icon={Package} accent={stats?.low_parts > 0 ? "text-[#F5A623]" : "text-white"} testid="kpi-lowparts" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div data-testid="chart-pm-trend" className="border border-white/10 bg-[#121212]">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">30-Day Analytics</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">PM Completion Trend</h2>
          </div>
          <div className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }} interval={4} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <YAxis tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                  itemStyle={{ color: "#E0E0E0" }}
                  labelStyle={{ color: "#A3A3A3" }}
                />
                <Line type="monotone" dataKey="pm_count" stroke="#007AFF" strokeWidth={2} dot={{ r: 2, fill: "#007AFF" }} name="PM Records" />
                <Line type="monotone" dataKey="fails" stroke="#FF3B30" strokeWidth={2} dot={{ r: 2, fill: "#FF3B30" }} name="Failures" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="chart-fault-type" className="border border-white/10 bg-[#121212]">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">Reliability</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">Faults by Equipment Type</h2>
          </div>
          <div className="p-4 h-72">
            {faultData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#A3A3A3] text-sm">
                No fault data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={faultData} margin={{ top: 10, right: 20, left: -10, bottom: 40 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis
                    dataKey="type"
                    tick={{ fill: "#A3A3A3", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  />
                  <YAxis tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                    itemStyle={{ color: "#E0E0E0" }}
                    labelStyle={{ color: "#A3A3A3" }}
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  />
                  <Bar dataKey="total" name="Total Faults">
                    {faultData.map((entry, i) => (
                      <Cell key={i} fill={TypeBarColors[i % TypeBarColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-white/10 bg-[#121212]">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">Priority Queue</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">Today&apos;s PM Tasks</h2>
          </div>
          <div data-testid="today-tasks">
            {stats?.today_equipment?.length === 0 && (
              <div className="px-6 py-12 text-center text-[#A3A3A3] text-sm">Add equipment to see PM tasks.</div>
            )}
            {stats?.today_equipment?.map((e) => (
              <div key={e.id} className="px-6 py-3 border-b border-white/5 last:border-b-0 flex items-center justify-between hover:bg-white/[0.02]">
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{e.location_name} · {e.type}</div>
                  <div className="font-mono-tab text-[11px] text-[#737373] mt-0.5 tracking-wider">{e.asset_id.toUpperCase()}</div>
                </div>
                <span className="inline-block px-2 py-0.5 text-[10px] font-mono-tab tracking-[0.15em] uppercase border border-[#F5A623]/30 bg-[#F5A623]/15 text-[#F5A623]">
                  Check Due
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212]">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">Event Feed</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">Recent PM Records</h2>
          </div>
          <div data-testid="recent-pm">
            {stats?.recent_pm?.length === 0 && (
              <div className="px-6 py-12 text-center text-[#A3A3A3] text-sm">No PM records yet.</div>
            )}
            {stats?.recent_pm?.map((p) => (
              <div key={p.id} className="px-6 py-3 border-b border-white/5 last:border-b-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{p.interval} PM · {p.tech || "—"}</div>
                    <div className="font-mono-tab text-[11px] text-[#737373] mt-0.5 tracking-wider">
                      <span className="text-[#22C55E]">{p.pass_count} PASS</span> ·{" "}
                      <span className="text-[#FF3B30]">{p.fail_count} FAIL</span> ·{" "}
                      <span className="text-[#A3A3A3]">{p.na_count} N/A</span>
                    </div>
                  </div>
                  <div className="font-mono-tab text-xs text-[#E0E0E0]">{p.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
